import clsx from "clsx";
import { AlertIcon, AlertVariant, Button, ButtonProps, IconName } from "@/components/common";
import { DefaultComponentProps } from "@/interfaces/common";
import { ModalBase } from "../ModalBase";

export type ModalAction = [string, (() => void)?] | ButtonProps;

interface AlertProps {
  variant: AlertVariant;
  icon?: IconName;
}

interface Props extends DefaultComponentProps {
  isOpen: boolean;
  onClose?: () => void;
  title: string;
  description?: string;
  actions?: ModalAction[];
  actionsClassName?: string;
  fullWidthActionsOnMobile?: boolean;
  cancelable?: boolean;
  isOverlayCloseable?: boolean;
  alertIcon?: AlertProps;
  hasCloseButton?: boolean;
}

export const ModalAlert = ({
  className,
  isOpen,
  onClose,
  title,
  description,
  actions = [],
  actionsClassName,
  fullWidthActionsOnMobile = false,
  alertIcon,
  cancelable,
  isOverlayCloseable = true,
  hasCloseButton = false,
  children,
}: Props): JSX.Element => {
  const defaultActions: Record<number, ButtonProps> = {
    0: {
      variant: "secondary",
      onClick: onClose,
      className: "!w-full justify-center",
    },
    1: {
      variant: "primary",
      className: "!w-full justify-center",
    },
  };
  const safeActions = [...actions];

  if (cancelable) {
    safeActions.unshift(["Cancelar", onClose]);
  }

  const actionsGrouped = safeActions.reduce<ButtonProps[][]>((group, action, index) => {
    const isEven = index % 2 === 0;
    const defaults = defaultActions[isEven ? 0 : 1] ?? {};

    const props = Array.isArray(action)
      ? { ...defaults, children: action[0], onClick: action[1] }
      : { ...defaults, ...action, className: clsx(defaults.className, action.className) };

    if (isEven) {
      group.push([props]);
    } else {
      group[group.length - 1].push(props);
    }

    return group;
  }, []);

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      isOverlayCloseable={isOverlayCloseable}
      hasCloseButton={hasCloseButton}
      className={clsx([
        "modal-alert mx-4 flex w-full max-w-[550px] flex-col items-center bg-white p-8 shadow-xl md:mx-auto",
        className,
      ])}
    >
      {alertIcon && <AlertIcon className="mb-6" variant={alertIcon.variant} icon={alertIcon.icon ?? null} />}

      <div className={clsx("flex flex-col items-center text-center gap-4", { "mb-4": children })}>
        <h3>{title}</h3>

        {description && <p>{description}</p>}
      </div>

      <div className={clsx("w-full text-left mb-8", { "mb-4": !actionsGrouped.length })}>{children}</div>

      <div className={clsx("w-full", actionsClassName)}>
        {actionsGrouped.flatMap((group, groupIndex) => {
          return (
            group.some(Boolean) && (
              <div key={groupIndex} className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
                {group.map((action, actionIndex) => {
                  return (
                    <Button
                      {...action}
                      key={`${groupIndex}-${actionIndex}`}
                      className={clsx(
                        "w-full justify-center h-12",
                        { "!max-w-none": fullWidthActionsOnMobile },
                        action.className,
                      )}
                    >
                      {action.children}
                    </Button>
                  );
                })}
              </div>
            )
          );
        })}
      </div>
    </ModalBase>
  );
};
